import ChevronLeft from '@remote-com/norma/icons/chevron-left.svg';
import ChevronRight from '@remote-com/norma/icons/chevron-right.svg';
import Image from 'next/image';
import { RichText } from 'prismic-reactjs';
import ContentContainer from 'remote-com-shared/components/ContentContainer';
import { fluidProp, whenProp } from 'remote-com-shared/style/helpers';
import { AnimatedSection, sharedTransition } from 'remote-com-shared/style/motion';
import styled, { css } from 'styled-components';
import SwiperCore, { Autoplay, EffectCoverflow, Navigation } from 'swiper/core';
import { SwiperSlide, Swiper } from 'swiper/react';

import 'swiper/swiper.min.css';
import 'swiper/components/effect-coverflow/effect-coverflow.min.css';

import {
  SectionWrapper,
  HeartImage,
  StarImage,
  SectionLabel as SectionLabelComponent,
  TitleComponent,
  UserImage,
  UserName,
  UserCompany,
  BottomLogoWrapper,
} from './index';

import { tabletBreakpoint, mobileBreakpoint } from '@/src/domains/shared/breakpoints';
import { AnimatedSectionWrapper, defaultAnimation } from '@/src/style/motion';

SwiperCore.use([EffectCoverflow, Navigation, Autoplay]);

const SectionContent = styled.div``;

export const SectionLabel = styled(SectionLabelComponent)`
  text-align: left;
  padding-left: 13px;

  ${tabletBreakpoint(css`
    padding-left: 20px;
  `)};
`;

export const SectionTitle = styled(TitleComponent)`
  text-align: left;
  padding-left: 10px;

  ${tabletBreakpoint(css`
    width: 50%;
    padding-left: 17px;
  `)};
`;

export const CompanyValuesSwiper = styled(Swiper)`
  position: relative;
  padding-top: 70px;
  padding-bottom: 70px;

  .swiper-button-prev,
  .swiper-button-next {
    position: absolute;
    top: 0;
    width: 51px;
    height: 51px;
    border-radius: 6px;
    border: 1.5px solid var(--colors-turquoise);
    box-sizing: border-box;
    border-radius: 50%;

    // Increased click area
    &::after {
      content: '';
      display: block;
      width: 44px;
      height: 44px;
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }
  }

  .swiper-button-prev {
    right: 30%;
    transform: translateX(-25px);
  }
  .swiper-button-next {
    right: 29%;
    transform: translateX(25px);
  }

  .swiper-slide {
    width: 466px;
  }

  ${defaultAnimation}
`;

export const Quote = styled.div`
  ${({ theme }) => theme.typography.body}
  min-height: 350px;
  position: relative;
  color: var(--colors-lynch);
  ${sharedTransition(['opacity, background-color, color'])}
  ${fluidProp('padding-top', '28px', '36px')}
  ${fluidProp('padding-bottom', '34px', '48px')}
  ${fluidProp(['padding-left', 'padding-right'], '32px', '40px')}

  > * {
    font-size: inherit;
    font-weight: inherit;
    margin: inherit;
  }

  ${whenProp(
    '$longQuote',
    css`
      min-height: 400px;
    `
  )}
`;

export const QuoteWrapper = styled.div`
  max-width: 466px;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 0 auto;
  padding: 0 10px;
  ${sharedTransition('background-color, opacity')}

  ${Quote} {
    background-color: var(--colors-blank);
    box-shadow: 5px 12px 26px rgba(50, 36, 138, 0.12);
    border-radius: 13px;
    color: var(--colors-bayoux);
    opacity: 1;

    path {
      fill: var(--colors-blank);
    }
  }
`;

export const UserWrapper = styled.div`
  margin-bottom: 38px;
`;

export const CardUserName = styled(UserName)`
  color: var(--colors-darkBlue);
`;

export const CardUserCompany = styled(UserCompany)`
  color: var(--colors-irisBlue);
`;

export const ControlButton = styled.button`
  width: 51px;
  height: 51px;
  border: 1.5px solid var(--colors-turquoise);
  border-radius: 50%;
  background: transparent;
  display: flex;
  justify-content: center;
  align-items: center;
  color: var(--colors-turquoise);
  z-index: 2;

  &:hover {
    border-color: ${({ theme }) => theme.colors.turquoise_60};
  }

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: 1px auto var(--colors-irisBlue);
  }
`;

export const NavigationControls = styled.div`
  && {
    position: absolute;
    top: 20%;
    right: 17%;
    display: flex;

    > * + * {
      margin-left: 15px;
    }

    ${mobileBreakpoint(css`
      right: 7%; // overlay on small screens
    `)};
  }
`;

export const QuoteBlock = ({
  client_image: clientImage,
  client_bottom_image: clientBottomImage,
  quote,
  name,
  company,
  company_url: companyURL,
  highlight_name: highlightName,
  longQuote,
  ...props
}) => {
  const companyURLProps = companyURL
    ? { as: 'a', href: companyURL?.url, target: '_blank', rel: 'noreferrer', $link: true }
    : null;
  const highlightNameProps = highlightName ? { $highlightName: true } : null;
  return (
    <QuoteWrapper {...props}>
      <Quote $longQuote={longQuote}>
        <UserWrapper>
          {clientImage && (
            <UserImage src={clientImage.url} alt={clientImage.alt ?? name} height={64} width={64} />
          )}

          <RichText render={name} Component={CardUserName} {...highlightNameProps} />

          <RichText render={company} Component={CardUserCompany} {...companyURLProps} />

          {clientBottomImage && (
            <BottomLogoWrapper>
              <Image
                src={clientBottomImage.url}
                alt={clientBottomImage.alt ?? name}
                height={clientBottomImage.dimensions.height}
                width={clientBottomImage.dimensions.width}
              />
            </BottomLogoWrapper>
          )}
        </UserWrapper>
        <RichText render={quote} />
      </Quote>
    </QuoteWrapper>
  );
};

export const TestimonialsCards = ({
  label,
  title,
  testimonials,
  swiperProps = {},
  starImage,
  longQuote = false,
}) => {
  const isTitleString = typeof title === 'string';

  return (
    <SectionWrapper>
      {starImage ? <StarImage /> : <HeartImage />}
      <SectionContent>
        <AnimatedSection ComponentName={AnimatedSectionWrapper}>
          <ContentContainer>
            {label && (
              <SectionLabel>
                <small>{label}</small>
              </SectionLabel>
            )}
            {isTitleString ? (
              <TitleComponent as="h2">{title}</TitleComponent>
            ) : (
              <RichText render={title} Component={SectionTitle} />
            )}
          </ContentContainer>
        </AnimatedSection>
        <AnimatedSection
          ComponentName={CompanyValuesSwiper}
          effect="coverflow"
          grabCursor
          centeredSlides
          coverflowEffect={{
            rotate: 0,
            stretch: 0,
            depth: 0,
            modifier: 1,
            slideShadows: false,
          }}
          loop
          loopedSlides={3}
          navigation={{
            nextEl: '#swiper-button-next',
            prevEl: '#swiper-button-prev',
          }}
          slidesPerView={1}
          breakpoints={{
            480: {
              slidesPerView: 'auto',
            },
          }}
          {...swiperProps}
        >
          {testimonials.map((obj, index) => (
            <SwiperSlide key={index}>
              {({ isActive, isPrev, isNext, isDuplicate }) => (
                <QuoteBlock
                  {...obj}
                  longQuote={longQuote}
                  isActive={isActive}
                  isPrev={isPrev}
                  isNext={isNext}
                  isDuplicate={isDuplicate}
                />
              )}
            </SwiperSlide>
          ))}
        </AnimatedSection>
      </SectionContent>
      <NavigationControls>
        <ControlButton type="button" id="swiper-button-prev">
          <ChevronLeft />
        </ControlButton>
        <ControlButton type="button" id="swiper-button-next">
          <ChevronRight />
        </ControlButton>
      </NavigationControls>
    </SectionWrapper>
  );
};
